import type {CommsClient,CommsServer} from './commsTypes';
import type {EnduranceState} from '../../../shared/endurance.mjs';
import {MovementSync} from './movementSync';
export const PROTOCOL_VERSION = 1 as const;
import type {CharacterState} from '../game/character';
export type Trade={id:string;players:[string,string];status:'invited'|'active'|'complete'|'cancelled';offers:Record<string,{bie:number;items:Record<string,number>}>;confirmed:string[];expires:number};

export type OnlineMail={id:string;kind:'starter-pet-box'|'system';title:string;claimed:boolean;createdAt:number};
export type OnlineGroundDrop={id:string;itemId:string;quantity:number;ownerId:string|null;mapId:string;x:number;createdAt:number;freeAt:number;expiresAt:number};
export type OnlinePlayer = {
  combatStats?:import('../../../shared/combatStats.mjs').CombatStats;
  campaign?:import('../../../shared/campaign.mjs').CampaignState;
  travelEncounter?:{area:string;distance:number;nextDistance:number;cooldownUntil:number};
  storyBattle?:import('../../../shared/storyBattle.mjs').StoryBattle;
  journey?:import('../../../shared/journeys.mjs').JourneyState;
  pose?:'stand'|'sit'|'lie';
  respawnAt?:number;
  endurance?:EnduranceState;
  id: string;
  name: string;
  x: number;
  lastMoveSeq?:number;
  mapId?:string;depth?:number;height?:number;profile?:CharacterState;inventory?:Record<string,number>;hp?:number;
  trainingDefeats?:number;advancementQuest?:boolean;spiritRite?:boolean;mana?:number;wardUntil?:number;
  pet?:'dog'|'cat'|'spirit';petOwned?:('dog'|'cat'|'spirit')[];mailbox?:OnlineMail[];autoPlay?:boolean;autoPlaySince?:number;actionSkill?:string;
  templeBlessingUntil?:number;ghostShowRewards?:string[];
  spiritSpecimen?:string;
  cooldowns?:Record<string,number>;
  facing?:-1|1; actionAt?:number; actionKind?:'slash'|'cast'|'dodge'; dodgeUntil?:number;
  progression: {
    level: number;
    exp: number;
    bie: number;
    spiritEssence: number;
  };
};

export type OnlineEnemy = {
  combatFeedback?:{id:string;kind:'damage'|'critical'|'miss';damage:number};
  id: string;
  kind: 'pop' | 'krasue' | 'tanee' | 'mae-tanee' | 'krahang' | 'violent-dead';
  x: number;
  hp: number;
  maxHp: number;
  boss: boolean;
  mapId?:string;
  activity?:{mode:'idle'|'walk'|'windup'|'attack';vx:number;depth:number;facing:-1|1;actionAt:number};
  archetypeId?:string;displayName?:string;visualIndex?:number;headSignature?:string;
};

export type RoomSnapshot = {
  ghostMarket?:{stock:Record<string,number>};
  worldCycle?:import('../../../shared/roadEvents.mjs').WorldCycle;
  roomId: string;
  seq: number;
  haunting: number;
  bossActive: boolean;
  cleansingUntilMs: number | null;
  cycle: number;
  players: OnlinePlayer[];
  enemies: OnlineEnemy[];
  ghostDeaths?: (OnlineEnemy & {expiresAt:number})[];
  groundDrops?:OnlineGroundDrop[];
  trades?:Trade[];
  villages?:{id:string;name:string;leader:string;members:string[];invites:Record<string,number>;house:{mapId:string;x:number;level:number;hp:number;maxHp:number}|null}[];
  villageWars?:{id:string;clans:[string,string];teams:[string[],string[]];ready:string[];hp:Record<string,number>;status:'invited'|'active'|'finished'|'cancelled';expires:number;winner?:string}[];
  parties?:{id:string;leader:string;members:string[];invites:Record<string,number>}[];
  pvpMatches?:{id:string;mode:'solo'|'party';teams:[string[],string[]];captains:[string,string];status:'invited'|'active'|'finished'|'cancelled';expires:number;hp:Record<string,number>;ready:string[];winner?:number}[];
};

export type GuestBootstrapSession = {
  identityId: string;
  characterId: string;
  name: string;
  resumed: boolean;
};

export type GuestBootstrapLockManager = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

export type SessionFeedback =
 | {kind:'reward';id:string;playerId:string;exp:number;bie:number;spirit:number}
 | {kind:'result';id:string;action:string;value:string}
 | {kind:'error';id:string;text:string};

import {CONFIRM_ACTIONS,type ConfirmationQuote} from '../../../shared/confirmation.mjs';
type ServerMessage =
  | { v: 1; type: 'welcome'; playerId: string; roomId: string; snapshot: RoomSnapshot }
  | { v: 1; type: 'snapshot'; snapshot: RoomSnapshot }
  | { v: 1; type: 'reward'; eventId:string; playerId: string; enemyKind?: OnlineEnemy['kind']; exp: number; bie: number; spirit: number }
  | { v: 1; type: 'pong'; sentAt: number; serverAt: number }
  | {v:1;type:'command-result';eventId:string;action:string;value:string}
  | {v:1;type:'confirmation-quote';requestId:string;quote:ConfirmationQuote|null}
  | { v: 1; type: 'error'; eventId?:string; code: string; message: string }
  | CommsServer;

export type OnlineStatus = 'idle' | 'connecting' | 'online' | 'offline' | 'error';

export type OnlineState = {
  status: OnlineStatus;
  playerId: string | null;
  roomId: string | null;
  name: string | null;
  snapshot: RoomSnapshot | null;
  latencyMs: number | null;
  error: string | null;
};

type Listener = (state: Readonly<OnlineState>) => void;

export function resolveWebSocketUrl(locationLike: Pick<Location, 'protocol' | 'hostname' | 'host' | 'search'>): string {
  const params = new URLSearchParams(locationLike.search);
  const explicit = params.get('ws');
  if (explicit && /^(wss?|https?):\/\//i.test(explicit)) {
    return explicit.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
  }
  if (locationLike.protocol === 'https:') {
    return 'wss://' + locationLike.host + '/game-ws';
  }
  return 'ws://' + locationLike.host + '/game-ws';
}

export function resolveGuestBootstrapUrl(webSocketUrl: string): string {
  const url = new URL(webSocketUrl);
  url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
  url.pathname = '/session/guest';
  url.search = '';
  url.hash = '';
  return url.toString();
}

function browserGuestBootstrapLock(): GuestBootstrapLockManager | null {
  if (typeof navigator === 'undefined' || !navigator.locks) return null;
  const locks = navigator.locks;
  return {
    request: async <T>(name: string, callback: () => Promise<T>): Promise<T> =>
      await locks.request(name, async () => await callback()),
  };
}

async function performGuestBootstrap(
  webSocketUrl: string,
  fetchImpl: typeof fetch,
): Promise<GuestBootstrapSession> {
  const response = await fetchImpl(resolveGuestBootstrapUrl(webSocketUrl), {
    method: 'POST',
    credentials: 'include',
    headers: {
      accept: 'application/json',
    },
  });

  if (!response.ok) {
    let message = 'เริ่ม guest session ไม่สำเร็จ';
    try {
      const body = await response.json() as { message?: unknown };
      if (typeof body.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    } catch {
      // Keep the stable user-facing fallback when the server does not return JSON.
    }
    throw new Error(message);
  }

  return await response.json() as GuestBootstrapSession;
}

export function createGuestBootstrapper(
  lockManager: GuestBootstrapLockManager | null = browserGuestBootstrapLock(),
): (
  webSocketUrl: string,
  fetchImpl?: typeof fetch,
) => Promise<GuestBootstrapSession> {
  let inFlight: Promise<GuestBootstrapSession> | null = null;

  return async (
    webSocketUrl: string,
    fetchImpl: typeof fetch = fetch,
  ): Promise<GuestBootstrapSession> => {
    if (inFlight) return await inFlight;

    const run = async (): Promise<GuestBootstrapSession> =>
      await performGuestBootstrap(webSocketUrl, fetchImpl);

    inFlight = lockManager
      ? lockManager.request('phi-siam-guest-bootstrap', run)
      : run();

    try {
      return await inFlight;
    } finally {
      inFlight = null;
    }
  };
}

const defaultGuestBootstrapper = createGuestBootstrapper();

export async function bootstrapGuestSession(
  webSocketUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GuestBootstrapSession> {
  return await defaultGuestBootstrapper(webSocketUrl, fetchImpl);
}

export class OnlineSession {
  private readonly moveSync = new MovementSync();
  consumeMoveCorrection(player: OnlinePlayer): {x:number;depth:number;height:number}|null {
    return this.moveSync.consumeCorrection({x:player.x,depth:player.depth??0,height:player.height??0,mapId:player.mapId,lastMoveSeq:player.lastMoveSeq});
  }
  private feedbackListeners=new Set<(event:SessionFeedback)=>void>();
  private feedbackOwner:string|null=null;
  private feedbackSeen=new Set<string>();
  onFeedback(listener:(event:SessionFeedback)=>void):()=>void {this.feedbackListeners.add(listener);return()=>this.feedbackListeners.delete(listener);}
  private emitFeedback(event:SessionFeedback):void {if(!event.id||this.feedbackSeen.has(event.id))return;this.feedbackSeen.add(event.id);for(const listener of this.feedbackListeners)listener(event);}
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private socialListeners=new Set<(message:CommsServer)=>void>();
  private confirmationHandler:((action:string,value:string,commit:(id:string)=>void)=>void)|undefined;
  private quoteRequests=new Map<string,{resolve:(quote:ConfirmationQuote|null)=>void;timer:ReturnType<typeof setTimeout>}>();
  installConfirmation(handler:(action:string,value:string,commit:(id:string)=>void)=>void):()=>void{this.confirmationHandler=handler;return ()=>{if(this.confirmationHandler===handler)this.confirmationHandler=undefined;};}
  requestConfirmation(action:string,value:string):Promise<ConfirmationQuote|null>{
    if(!this.connected)return Promise.resolve(null);
    const requestId=crypto.randomUUID();return new Promise(resolve=>{
      const timer=setTimeout(()=>{this.quoteRequests.delete(requestId);resolve(null);},5000);
      this.quoteRequests.set(requestId,{resolve,timer});this.send({v:1,type:'confirmation-quote',requestId,action,value});
    });
  }
  onSocial(listener:(message:CommsServer)=>void):()=>void{this.socialListeners.add(listener);return ()=>this.socialListeners.delete(listener);}
  sendSocial(message:CommsClient):void{this.send(message);}
  private pingTimer: number | null = null;
  private state: OnlineState = {
    status: 'idle',
    playerId: null,
    roomId: null,
    name: null,
    snapshot: null,
    latencyMs: null,
    error: null,
  };

  get current(): Readonly<OnlineState> {
    return this.state;
  }

  get connected(): boolean {
    return this.state.status === 'online' && this.socket?.readyState === WebSocket.OPEN;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async connect(roomId: string, name: string, url = resolveWebSocketUrl(window.location)): Promise<Readonly<OnlineState>> {
    this.disconnect(false);
    const safeRoom = roomId.trim().toUpperCase().replace(/[^A-Z0-9ก-๙_-]/g, '').slice(0, 20) || 'BANG-PHI-01';
    const safeName = name.trim().slice(0, 24) || 'ศิษย์ไร้นาม';

    this.patch({
      status: 'connecting',
      roomId: safeRoom,
      name: safeName,
      playerId: null,
      snapshot: null,
      latencyMs: null,
      error: null,
    });

    try {
      await bootstrapGuestSession(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'เริ่ม guest session ไม่สำเร็จ';
      this.patch({ status: 'error', error: message });
      throw error;
    }

    return await new Promise((resolve, reject) => {
      const socket = new WebSocket(url);
      this.socket = socket;
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        socket.close();
        const error = new Error('เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ');
        this.patch({ status: 'error', error: error.message });
        reject(error);
      }, 5000);

      socket.addEventListener('open', () => {
        socket.send(JSON.stringify({
          v: PROTOCOL_VERSION,
          type: 'join',
          roomId: safeRoom,
          name: safeName,
        }));
      });

      socket.addEventListener('message', (event) => {
        if(this.socket!==socket)return;
        let message: ServerMessage;
        try {
          message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }

        if (message.type === 'welcome') {
          if(this.feedbackOwner!==message.playerId){this.feedbackOwner=message.playerId;this.feedbackSeen.clear();}
          clearTimeout(timeout);
          settled = true;
          this.patch({
            status: 'online',
            playerId: message.playerId,
            roomId: message.roomId,
            snapshot: message.snapshot,
            error: null,
          });
          this.startPing();
          resolve(this.state);
          return;
        }

        if(['chat','social-state','voice-signal','social-error'].includes(message.type)){for(const listener of this.socialListeners)listener(message as CommsServer);return;}

        if(message.type==='reward'){
          if(message.playerId===this.state.playerId&&typeof message.eventId==='string'&&[message.exp,message.bie,message.spirit].every(n=>Number.isFinite(n)&&n>=0))this.emitFeedback({kind:'reward',id:message.eventId,playerId:message.playerId,exp:message.exp,bie:message.bie,spirit:message.spirit});
          return;
        }
        if(message.type==='confirmation-quote'){
          const pending=this.quoteRequests.get(message.requestId);if(pending){clearTimeout(pending.timer);this.quoteRequests.delete(message.requestId);pending.resolve(message.quote);}return;
        }
        if(message.type==='command-result'&&typeof message.eventId==='string'&&typeof message.action==='string'&&typeof message.value==='string'){this.emitFeedback({kind:'result',id:message.eventId,action:message.action,value:message.value});return;}

        if (message.type === 'snapshot') {
          // WebSocket frames are ordered, but async room work can publish an older room revision.
          if (this.state.snapshot && message.snapshot.seq < this.state.snapshot.seq) return;
          this.patch({ snapshot: message.snapshot,error:null });
          return;
        }

        if (message.type === 'pong') {
          this.patch({ latencyMs: Math.max(0, Date.now() - message.sentAt) });
          return;
        }

        if (message.type === 'error') {
          this.emitFeedback({kind:'error',id:message.eventId??crypto.randomUUID(),text:message.message});
          this.patch({ error: message.message });
        }
      });

      socket.addEventListener('close', () => {
        if (this.socket !== socket) return;
        this.stopPing();
        const wasOnline = this.state.status === 'online';
        this.socket = null;
        this.patch({
          status: wasOnline ? 'offline' : 'error',
          error: wasOnline ? 'การเชื่อมต่อหลุด' : (this.state.error ?? 'เชื่อมต่อไม่ได้'),
        });
        if (!settled) {
          clearTimeout(timeout);
          settled = true;
          reject(new Error(this.state.error ?? 'เชื่อมต่อไม่ได้'));
        }
      });

      socket.addEventListener('error', () => {
        if (!settled) this.patch({ error: 'WebSocket error' });
      });
    });
  }

  disconnect(updateState = true): void {
    this.moveSync.reset();
    for(const pending of this.quoteRequests.values()){clearTimeout(pending.timer);pending.resolve(null);}this.quoteRequests.clear();
    this.stopPing();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close();
    if (updateState) {
      this.patch({
        status: 'offline',
        playerId: null,
        snapshot: null,
        latencyMs: null,
        error: null,
      });
    }
  }

  sendMove(worldX: number,depth=0,height=0,mapId?:string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const moveSeq = this.moveSync.record({x:worldX,depth,height,mapId});
    this.send({ v: PROTOCOL_VERSION, type: 'move', x: worldX,depth,height,moveSeq,...(mapId?{mapId}:{}) });
  }
  command(action:string,value:string,onSend?:()=>void):void {
    if(this.confirmationHandler&&CONFIRM_ACTIONS.has(action)){
      const owner=this.state.playerId;let sent=false;this.confirmationHandler(action,value,id=>{if(!sent&&this.connected&&this.state.playerId===owner){sent=true;onSend?.();this.send({v:1,type:'command',action,value,quoteId:id});}});return;
    }
    onSend?.();this.send({v:1,type:'command',action,value});
  }

  sendAttack(enemyId: string, action: 'basic' | 'skill-1' | 'skill-2' | 'skill-3'): void {
    this.send({ v: PROTOCOL_VERSION, type: 'attack', enemyId, action });
  }

  private send(payload: unknown): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(payload));
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ v: PROTOCOL_VERSION, type: 'ping', sentAt: Date.now() });
    }, 3000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private patch(patch: Partial<OnlineState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}

export const onlineSession = new OnlineSession();
